/**
 * The escape hatches every layer that covers the page owes its reader.
 *
 * MOVED OUT OF `Dialog.tsx` WHEN A SECOND AND THIRD LAYER ARRIVED. The tool editor now covers
 * the whole screen and the Try your agent drawer covers its right edge, and neither is a
 * centred panel - but both owe the same four things `Dialog` always gave: Escape closes,
 * focus goes in on open and back to the opener on close, Tab stays inside, and the page
 * behind stops scrolling. That file's header argued a second hand-rolled dialog would get
 * one of those slightly wrong; this is the answer to that, one level down.
 *
 * THE NOTES THAT EXPLAIN EACH LINE ARE THE ONES THAT WERE IN `Dialog.tsx`, kept where the
 * lines now live.
 */

import { useEffect, useRef, type RefObject } from "react";

/** Everything a browser will put a focus ring on, in DOM order. */
export const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  // `summary` IS FOCUSABLE AND WAS MISSING, which was invisible until a dialog held one:
  // a Tab from the transcript's last "Show all" left the panel for the page behind.
  'summary, textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useModalLayer(panel: RefObject<HTMLElement | null>, onClose: () => void) {
  /*
   * Who to give focus back to, captured DURING THE FIRST RENDER and not in an effect.
   * `autoFocus` inside the layer is applied in the commit phase, before a passive effect
   * runs, so an effect would record the layer's own first field as "the opener" and hand
   * focus back to an element that no longer exists. The `=== null` guard is what makes the
   * render-phase initialiser safe under StrictMode's double render.
   */
  const opener = useRef<HTMLElement | null>(null);
  if (opener.current === null) opener.current = document.activeElement as HTMLElement | null;

  useEffect(() => {
    const node = panel.current;
    // The panel itself, so a screen reader hears its name first - unless a field inside has
    // already taken focus with `autoFocus`.
    if (!node?.contains(document.activeElement)) node?.focus();
    return () => {
      // StrictMode's simulated remount runs this cleanup with the panel still attached;
      // restoring focus then would yank it out of the second mount's field. A real close
      // detaches the panel first.
      if (node?.isConnected) return;
      opener.current?.focus?.();
    };
  }, [panel]);

  // The page must not scroll behind a layer that covers it.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // A list open inside the layer takes Escape first: closing a menu must not also ask
        // to throw the whole panel away. This listener runs in the capture phase, before the
        // list could stop it, so the list is recognised by its mark instead.
        if (popupOpen()) return;
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const fields = Array.from(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (!fields.length) return;
      const first = fields[0]!;
      const last = fields[fields.length - 1]!;
      // The panel is focusable and is where focus starts, so both ends are wrapped here.
      const active = document.activeElement;
      if (event.shiftKey ? active === first || active === panel.current : active === last) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [panel, onClose]);
}

/**
 * Whether the focus is in a list that is open inside the layer: a `Listbox` (which marks its
 * root `data-popup-open`) or CodeMirror's completions (whose content names the list it
 * controls only while one is showing).
 */
function popupOpen(): boolean {
  const active = document.activeElement;
  if (!active) return false;
  if (active.closest("[data-popup-open]")) return true;
  return active.classList.contains("cm-content") && active.hasAttribute("aria-controls");
}
